import React, { useEffect, useState } from 'react';
import { getWarmupStatus } from '../../services/api/ocel.api';
import { cn } from '../../utils/cn';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import WarmupStatusPanel from './WarmupStatusPanel';

// Re-check while a warm pass is running; a completed/unknown status is stable
// until the file or the cache changes, so no polling is needed then.
const POLL_MS = 10000;

const STATUS_META = {
  running: { dot: 'bg-amber-400 animate-pulse', label: 'Cache wird aufgewärmt…' },
  completed: { dot: 'bg-green-500', label: 'Cache bereit' },
  unknown: { dot: 'bg-gray-300', label: 'Cache nicht vorbereitet' },
};

/**
 * Status dot for the current file's cache warm-up pass
 * (see backend/src/shared/cache_warming.py). Wrapped in HideInWorkshop at the
 * call site so it disappears in workshop mode. Doubles as a trigger for the
 * detailed per-tile WarmupStatusPanel, opened on click.
 */
const WarmupStatusIndicator = ({ activeFile }) => {
  const [record, setRecord] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setRecord(null);
    if (!activeFile) return undefined;
    let cancelled = false;
    let timer = null;
    const load = async () => {
      try {
        const res = await getWarmupStatus(activeFile);
        if (cancelled) return;
        setRecord(res);
        if (res?.status === 'running') {
          timer = setTimeout(load, POLL_MS);
        }
      } catch {
        if (!cancelled) setRecord(null); // endpoint missing/unauthorized — show nothing
      }
    };
    load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [activeFile]);

  if (!activeFile || !record) return null;

  const meta = STATUS_META[record.status] || STATUS_META.unknown;
  const title =
    record.status === 'completed' && record.finished_at
      ? `${(record.targets ?? 0) - (record.skipped ?? 0)}/${record.targets ?? 0} Ansichten vorberechnet (${new Date(record.finished_at).toLocaleTimeString()})`
      : meta.label;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2 px-3 py-1.5 text-xs text-gray-500 w-full hover:bg-gray-50 transition-colors"
          title={title}
        >
          <span className={cn('w-2 h-2 rounded-full flex-shrink-0', meta.dot)} />
          <span className="truncate">{meta.label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-96 p-0">
        <WarmupStatusPanel activeFile={activeFile} open={open} />
      </PopoverContent>
    </Popover>
  );
};

export default WarmupStatusIndicator;
